import React, { FC, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Dialog, DialogTitle, DialogContent } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { normalizePhone, displayPhone, validateIsraeliPhone, validateIsraeliId } from 'utils/israeliContact'
import { theme, Text, Row, Space, Icon, Image, Spinner } from 'ui'
import { Button, Radio, Checkbox, IconButton,  Select, InputLabel, MenuItem } from '@material-ui/core'
import { Edit, Delete, Add, Close, ChevronLeft, ChevronRight } from '@material-ui/icons'
 import { fetchUsers, fetchUserFamily, fetchTags, fetchUser, fetchTagTypes, User } from '../usersApi'
import { ValidatedPopulationInput } from '../ValidatedInput'
import { TextField } from '@material-ui/core'
import { observer } from 'mobx-react'
import useStore from 'store'

import Users from 'assets/icons/Users.svg'
import Users4 from 'assets/icons/ims/Users4.svg'

const SectionPopulation = styled.div`
  display: flex;
  padding: 2px 25.81px 2px 25.81px;
  flex-direction: column;
  align-items: flex-start;
  gap: 23.991px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 12px;
  border: 1.818px solid rgba(190, 219, 255, 0.50);
  background: linear-gradient(135deg, #EFF6FF 0%, #EEF2FF 100%);
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.10), 0 1px 2px -1px rgba(0, 0, 0, 0.10);    
`
const SectionPopulationContainer2 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 23.991px;
  flex-shrink: 0;
  align-self: stretch;
`
const SectionPopulationContainer = styled.div`
  display: flex;
  height: 53.793px;
  width: 100%;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  align-self: stretch;
  border-bottom: 1.818px solid #BEDBFF;
`
const SectionPopulationTitle = styled.div`
  display: flex;
  height: 53.793px;
  width: 100%;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  align-self: stretch;
  color: #1C398E;
  font-family: Rubik;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px; /* 140% */
`
// Wraps rather than dividing the row between however many fields it holds: a
// field keeps a readable width and drops to the next line when there is no room,
// so the four-across layout becomes two-across on a narrow window by itself.
// The old fixed height had to go with it - a wrapped row is taller.
// auto-fit collapses the tracks nothing lands in and hands their width back to
// the rest, so a four-field row stays four across and evenly sized down to a
// window around 820px rather than breaking into three-plus-one on the way.
const SectionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  align-items: start;
  gap: 15.994px;
  flex-shrink: 0;
  align-self: stretch;
`
// Notices span the form; they are not fields and must not be sized like one.
const NoticeRow = styled.div`
  display: flex;
  width: 100%;
  align-self: stretch;
`
const SectionRow2 = styled.div`
  flex-direction: column;
   display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15.994px;
  flex-shrink: 0;
  align-self: stretch;
`
const SectionRow3 = styled.div`
  display: flex;
  padding: 16.903px 0 0 0;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15.994px;
  align-self: stretch;
  border-top: 0.909px solid #BEDBFF;
  @media (max-width: 780px) {
    flex-direction: column;
  }
`
// Grows to fill the row but never past what the content needs - a name, a phone
// number and an id number all read fine at 300px and looked absurd at 450.
const StyledInputContainer = styled.div`
  min-width: 0;
`
// A handful of characters - house number, shelter - so the field itself is cut
// back inside its column. The column stays, which is what keeps the row below
// the others in line.
const NarrowInputContainer = styled(StyledInputContainer)`
  max-width: 160px;
`
const StyledSelect = styled.select`
  height: 42px;
  width: 100%;
  box-sizing: border-box;
  align-self: stretch;
  border-radius: 6px;
  border-right: 16px solid transparent;
  border: 0.909px solid #E2E8F0;
  background: #F8FAFC;
  &:disabled {
    border-color: #E2E8F0;
    background: #F1F5F9;
    color: #475569;
  }
`
const StyledLabel = styled.div`
  display: flex;
  /*padding-left: 258.452px;*/
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
`
const StyledRequired = styled.div`
  color: #DC2626;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; 
`
const ErrorFormMessage = styled.div`
  display: flex;
  min-height: 45.795px;
  width: 100%;
  padding: 11.989px 10px;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid #8EC5FF;
  background: #DBEAFE;
  color: #193CB8;
`
// Says why the personal details are read-only. Amber rather than the blue of
// ErrorFormMessage above it, which is the "fill one of these in" note - the two
// can show at the same time and say unrelated things.
const LockedFieldsNotice = styled.div`
  display: flex;
  width: 100%;
  padding: 12px 10px;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid #FCD34D;
  background: #FEF3C7;
  color: #92400E;
  font-family: Rubik;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: right;
`
const UserTypeTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  color: #64748B;
  text-align: right;
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`
const UserTypeListSelected = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`
const SelectedUserTypeButton = styled(Button)`
  display: flex;
  padding: 5px 12px;
  align-items: center;
  gap: 4px;
  border-radius: 30504000px;
  border: 0.909px solid rgba(37, 99, 235, 0.25);
  background: rgba(37, 99, 235, 0.06);
  color: #2563EB;
  text-align: center;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
` 
const UserTypeList = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  flex-wrap: wrap;
`
const UserTypeButton = styled(Button)`
  display: flex;
  padding: 5px 13px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  border: 0.909px solid #E2E8F0;
  background: rgba(241, 245, 249, 0.30);
`
const NewUserTypeButton = styled(Button)`
  color: #2563EB;
  text-align: center;
  font-family: Rubik;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`
const UserTypeCheckSection = styled.div`
  display: flex;
  height: 65.781px;
  width: 100%;
  padding: 0 15.994px;
  justify-content: flex-end;
  align-items: center;
  gap: 11.989px;
  border-radius: 8px;
  border: 0.909px solid #E5E7EB;
  background: #F9FAFB;
`
const UserTypeCheckSectionBlue = styled(UserTypeCheckSection)`
  border: 0.909px solid #BEDBFF;
  background: #EFF6FF;
`

const UserTypeCheckContainer = styled.div`
  display: flex;
  padding-bottom: 0;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  flex: 1 0 0;
`
const UserTypeCheckTitle1 = styled.div`
  color: #101828;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
`
const UserTypeCheckTitle2 = styled.div`
  color: #4A5565;
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`
const UserTypeCheckTitle3 = styled.div`
  color: #1C398E;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px; 
`
const UserTypeCheckTitle4 = styled.div`
  color: #155DFC;
  font-family: Rubik;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;  
`
const NewUserTypeContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 520px;
  justify-content: flex-end;
  align-items: flex-start;
  gap: 7.997px;
  flex-shrink: 0;
`
const NewUserTypeSaveButton = styled(Button)`
  display: flex;
  padding: 9.634px 15.943px 8.17px 15.994px;
  justify-content: center;
  align-items: center;
  color: #FFF;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  border-radius: 6px;
  background: #2563EB;
`
const NewUserTypeCancelButton = styled(Button)`
  display: flex;
  padding: 9.634px 15.074px 8.17px 15.994px;
  justify-content: center;
  align-items: center;
  color: #0F172A;
  text-align: center;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  border-radius: 6px;
  background: #F1F5F9;
  line-height: 24px;
`
const UserTypeCheckSectionContainer = styled.div`
  display: flex;
   width: 100%;
  justify-content: flex-end;
  align-items: center;
  flex-direction: column;
` 
const NewUserTypeInput = styled(TextField)`
  display: flex;
  height: 41.804px;
  padding: 0px;
  justify-content: flex-end;
  align-items: center;
  flex: 1 0 0;
  border-radius: 6px;
  border: 0.909px solid #E2E8F0;
  background: #F8FAFC;
  color: rgba(15, 23, 42, 0.50);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  .MuiOutlinedInput-root {
    height: 41.804px;
    padding: 0px;
    width 100%;
  }
  .MuiOutlinedInput-notchedOutline{
    height: 41.804px;
    border: 0px;
  }
`
const ImageIcon1 = styled(Image)`
  display: flex;
  width: 39.986px;
  height: 39.986px; 
  padding: 8px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  background: #2B7FFF;
`
const StyledDateInput = styled.input`
  display: flex;
  height: 41.804px;
  padding: 8px 16px;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 0.909px solid #D1D5DC;
  color: rgba(15, 23, 42, 0.70);
  text-align: right;
  font-family: Rubik;
  font-size: 16px;
  font-weight: 400;
  outline: none;
  width: 100%;
  box-sizing: border-box;
  &:disabled {
    border-color: #E2E8F0;
    background: #F1F5F9;
    color: #475569;
  }
`
const ContainerValue = styled.div`
  display: flex;
  height: 35px;
  padding: 8px 12px 8px 12px;
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.30);
`
export type PopulationData = {
  firstName: string
  middleName?: string
  lastName: string
  nickname?: string

  identityNumber: string
  birthDate: Date | null
  maritalStatus?: string
  gender?: string

  city: string
  street: string
  houseNumber: string
  shelter?: string

  email?: string
  phone?: string
  extraPhone?: string
  extraMobile?: string

  userTypes: any[]

  isLocalMember: boolean
  isDead: boolean
  deathDate?: Date | null
}

type Props = {
  value: PopulationData
  onChange: (value: PopulationData) => void,
  disableEdit: boolean,
  // The contact's global details belong to another community (see UserNewModal).
  // Only the fields spUser_Upsert refuses to write from here are affected; the
  // community's own fields below them stay editable.
  globalFieldsLocked?: boolean,
  setError: any,
  tags: any,
  tagTypes: any,
}

// The value written to the record stays Hebrew whatever the interface
// language is - it is data. Only the label the user reads is translated.
const MARITAL_STATUSES = [
  { value: 'רווק/ה', key: 'marital_single' },
  { value: 'נשוי/אה', key: 'marital_married' },
  { value: 'גרוש/ה', key: 'marital_divorced' },
  { value: 'אלמן/ה', key: 'marital_widowed' },
  { value: 'פרוד/ה', key: 'marital_separated' },
  { value: 'ידוע/ה בציבור', key: 'marital_partner' },
]

const GENDERS = [
  { value: 'זכר', key: 'gender_male' },
  { value: 'נקבה', key: 'gender_female' },
  { value: 'אחר', key: 'gender_other' },
]

const PopulationSection: FC<Props> = ({ value, onChange, disableEdit, globalFieldsLocked = false, setError, tags, tagTypes }) => {
  const { members, orgs, uiStore, memberStore } = useStore()
  const { t } = useTranslation('members')
  const [newTagName, setNewTagName] = useState('')
  const [userTypeList, setUserTypeList] = useState<any[]>([])
  const [validation, setValidation] = useState({
    'firstName': true,
    'lastName': true,
    'identityNumber': true,
    'houseNumber': true,
    'phone': true,
  })

  const [openNewUserTypeSection, setOpenNewUserTypeSection] = useState(false)
  const selectedUserTypes = value.userTypes
  const validationInitialized = useRef(false)

  useEffect(() => {
    const initialValidation = {
      firstName: !value.firstName?.trim(),
      lastName: !value.lastName?.trim(),
      identityNumber: !value.identityNumber?.trim() || !validateIsraeliId(value.identityNumber),
      phone: !value.phone?.trim() || !validateIsraeliPhone(value.phone),
    }
    
    setValidation(initialValidation)
    setError({ population: Object.values(initialValidation).some(v => v) })
  }, [])

  // When editing an existing user, value arrives asynchronously after mount.
  // Once we detect real data, compute initial validation from the actual field values.
  useEffect(() => {
    const initialValidation = {
      firstName: !value.firstName?.trim(),
      lastName: !value.lastName?.trim(),
      identityNumber: !value.identityNumber?.trim() || !validateIsraeliId(value.identityNumber),
      phone: !value.phone?.trim() || !validateIsraeliPhone(value.phone),
    }
    setValidation(initialValidation)
    setError({ population: Object.values(initialValidation).some(v => v) })
  }, [value])

  const addNewTag = async (tagName: any) => {
    const filteredTags = tags.data.filter((t: any) => t.tagName == tagName)
    if (filteredTags.length > 0) return 

    const selectedFilteredTags = selectedUserTypes.filter(t => t.tagName == tagName)
    if (selectedFilteredTags.length > 0) return 

    setUserTypeList(userTypeList.filter(item => item.tagName !== tagName))

    const tagType = tagTypes.find((item: any) => item.typeName == 'status')
    update({ userTypes: [...selectedUserTypes, { 
      tagName, 
      tagTypeId: tagType.id, 
      action: "new" 
    }] })
    
    setOpenNewUserTypeSection(false) 
    setNewTagName('')
  }

  const addUserTypeToList = async (tag: any) => {
    const selectedFilteredTags = selectedUserTypes.filter(t => t.tagName == tag.tagName)
    if (selectedFilteredTags.length > 0) return 

    setUserTypeList(userTypeList.filter(item => item.tagName !== tag.tagName))

    const tagType = tagTypes.find((item: any) => item.typeName == 'status')
    update({ userTypes: [...selectedUserTypes,  { 
      tagName: tag.tagName, 
      tagTypeId: tagType.id, 
      action: "fromList" 
    }] })

    setOpenNewUserTypeSection(false) 
    setNewTagName('')
  }

  const removeUserTypeFromList = (tag: any) => {
    setUserTypeList(prev => ([...prev,  { tagName: tag.tagName, tagType: "status" }]))
    update({ userTypes: selectedUserTypes.filter(t => {
      return t.tagName !== tag.tagName
    }) })
  }

  useEffect(() => {
    if (value.isDead && value.isLocalMember) {
      update({ isLocalMember: false })
    }
  }, [value.isDead])

  useEffect(() => {
      let mounted = true

      setUserTypeList(tags.data.filter((item: any) => item.tagType == 'status'))
      
      return () => {
        mounted = false
      }
  }, [tags])

  const update = (patch: Partial<PopulationData>) => {
    onChange({ ...value, ...patch })
  }

  const normalizeLandlinePhone = (v: string): string => {
    const stripped = v.replace(/-/g, '')
    return /^0\d{8}$/.test(stripped) ? '+972' + stripped.slice(1) : stripped
  }

  const displayLandlinePhone = (v: string): string => {
    const raw = v?.startsWith('+972') ? '0' + v.slice(4) : (v || '')
    return raw.length >= 2 ? raw.slice(0, 2) + '-' + raw.slice(2) : raw
  }

  const validateIsraeliLandlinePhone = (phone: string): boolean => {
    const stripped = phone.replace(/-/g, '')
    return /^\+972\d{8}$/.test(stripped) || /^0\d{8}$/.test(stripped)
  }


  // disableEdit is the whole-form read-only view; this is the narrower lock that
  // only covers the contact's global details.
  const globalDisabled = disableEdit || globalFieldsLocked

  const setValidationError = (patch: any) => {
    const updatedValidation = { ...validation, ...patch }
    setValidation(updatedValidation)
    setError({ population: Object.values(updatedValidation).filter(res => res).length > 0 })
  }

  return (
           <SectionPopulation>
                                   <SectionPopulationContainer>
                                        <ImageIcon1 src={Users4} />
                                       <SectionPopulationTitle>
                                           {t`population:population_section`}
                                       </SectionPopulationTitle>
                                   </SectionPopulationContainer>
                                   
                                   <SectionPopulationContainer2>
                                       {globalFieldsLocked && !disableEdit && (
                                         <NoticeRow>
                                           <LockedFieldsNotice>{t`population:notice_global_fields_other_community`}</LockedFieldsNotice>
                                         </NoticeRow>
                                       )}
                                       <SectionRow>
                                          <StyledInputContainer>
                                            <ValidatedPopulationInput
                                              id="input-id-1"
                                              label={t`population:given_name`}
                                              value={value.firstName}
                                              onChange={(v: any) => update({ firstName: v })}
                                              placeholder=""
                                              required={true}
                                              setValidationError={(error) => { setValidationError({ 'firstName': error }) }}
                                              disabled={disableEdit}
                                              locked={globalFieldsLocked}
                                              errorText={t`population:required_field`}
                                              icon={null}
                                            />      
                                          </StyledInputContainer>
                                            <StyledInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-2"
                                                label={t`population:middle_name`}
                                                value={value.middleName}
                                                onChange={(v: any) => update({ middleName: v })}
                                                placeholder=""
                                                required={false}
                                                disabled={disableEdit}
                                                locked={globalFieldsLocked}
                                                setValidationError={error => null}
                                                errorText=""
                                                icon={null}
                                              />    
                                           </StyledInputContainer>
                                           <StyledInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-3"
                                                label={t`population:family_name`}
                                                value={value.lastName}
                                                onChange={(v: any) => update({ lastName: v })}
                                                disabled={disableEdit}
                                                locked={globalFieldsLocked}
                                                placeholder=""
                                                required={true}
                                                setValidationError={(error) => { setValidationError({ 'lastName': error }) }}
                                                errorText={t`population:required_field`}
                                                icon={null}
                                              />     
                                           </StyledInputContainer>
                                           <StyledInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-4"
                                                label={t`population:nickname`}
                                                value={value.nickname}
                                                onChange={(v: any) => update({ nickname: v })}
                                                disabled={disableEdit}
                                                locked={globalFieldsLocked}
                                                placeholder=""
                                                required={false}
                                                setValidationError={error => null}
                                                errorText=""
                                                icon={null}
                                              />     
                                           </StyledInputContainer>
                                       </SectionRow>
                                       <SectionRow>
                                           <StyledInputContainer>
                                            <ValidatedPopulationInput
                                                id="input-id-5"
                                                label={t`population:identity_number_short`}
                                                value={value.identityNumber}
                                                onChange={(v: any) => update({ identityNumber: v })}
                                                disabled={disableEdit}
                                                locked={globalFieldsLocked}
                                                placeholder=""
                                                required={true}
                                                setValidationError={(error) => { setValidationError({ 'identityNumber': error }) }}
                                                customValidation={validateIsraeliId}
                                                errorText={t`population:identity_invalid`}
                                                icon={null}
                                              />         
                                           </StyledInputContainer>
                                            <StyledInputContainer>  
                                              <StyledLabel>{t`population:birth_date`}</StyledLabel>
                                              <StyledDateInput
                                                type="date"
                                                value={value.birthDate ? new Date(value.birthDate).toISOString().split('T')[0] : ''}
                                                onChange={e => update({ birthDate: e.target.value ? new Date(e.target.value) : null })}
                                                disabled={globalDisabled}
                                              />
                                           </StyledInputContainer>
                                           <StyledInputContainer>
                                              <StyledLabel>{t`population:marital_status`}</StyledLabel>
                                              {disableEdit ? <ContainerValue>{value.maritalStatus ?? '—'}</ContainerValue> : 
                                              <StyledSelect
                                                  autoFocus
                                                  disabled={disableEdit}
                                                  value={value.maritalStatus}
                                                  onChange={e => update({ maritalStatus: e.target.value })}
                                              >
                                                {MARITAL_STATUSES.map(item => <option value={item.value}>{t(`population:${item.key}`)}</option>)}
                                              </StyledSelect>}      
                                           </StyledInputContainer>
                                           <StyledInputContainer>
                                              <StyledLabel>{t`population:gender`}</StyledLabel>
                                              {disableEdit ? <ContainerValue>{value.gender ?? '—'}</ContainerValue> : 
                                              <StyledSelect
                                                autoFocus
                                                value={value.gender}
                                                disabled={globalDisabled}
                                                onChange={e => update({ gender: e.target.value })}
                                              > 
                                               {GENDERS.map(item => <option value={item.value}>{t(`population:${item.key}`)}</option>)}
                                              </StyledSelect>}
                                           </StyledInputContainer>
                                       </SectionRow>
           
                                       <SectionRow>
                                           <StyledInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-9"
                                                label={t`population:city`}
                                                value={value.city}
                                                onChange={(v: any) => update({ city: v })}
                                                disabled={disableEdit}
                                                placeholder=""
                                                required={false}
                                                setValidationError={error => { setValidationError({ 'city': error }) }}
                                                errorText={t`population:required_field`}
                                                icon={null}
                                              />    
                                            </StyledInputContainer>
                                            <StyledInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-10"
                                                label={t`population:street`}
                                                value={value.street}
                                                onChange={(v: any) => update({ street: v })}
                                                disabled={disableEdit}
                                                placeholder=""
                                                required={false}
                                                setValidationError={error =>  setValidationError({ 'street': error })}
                                                errorText={t`population:required_field`}
                                                icon={null}
                                              />     
                                           </StyledInputContainer>
                                           <NarrowInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-11"
                                                label={t`population:house_number`}
                                                value={value.houseNumber}
                                                onChange={(v: any) => update({ houseNumber: v })}
                                                disabled={disableEdit}
                                                placeholder=""
                                                required={false}
                                                setValidationError={error =>  setValidationError({'houseNumber': error }) }
                                                errorText={t`population:required_field`}
                                                icon={null}
                                              />     
                                           </NarrowInputContainer>
                                           <NarrowInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-12"
                                                label={t`population:shelter_number`}
                                                value={value.shelter}
                                                onChange={(v: any) => update({ shelter: v })}
                                                disabled={disableEdit}
                                                placeholder=""
                                                required={false}
                                                setValidationError={error => null}
                                                errorText=""
                                                icon={null}
                                              />    
                                           </NarrowInputContainer>
                                       </SectionRow>
           
                                       <NoticeRow>
                                           <ErrorFormMessage>
                                               <b>{t`population:note_heading`}</b>{t`population:note_one_of_fields`}<b>{t`population:mobile_or_email`}</b> <StyledRequired>*</StyledRequired>
                                           </ErrorFormMessage>
                                       </NoticeRow>
           
                                       <SectionRow>
                                           <StyledInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-14"
                                                label={t`population:mobile`}
                                                value={displayPhone(value.phone)}
                                                onChange={(v: any) => update({ phone: normalizePhone(v) })}
                                                disabled={disableEdit}
                                                placeholder=""
                                                required={false}
                                                setValidationError={error => setValidationError({ 'phone': error }) }
                                                customValidation={validateIsraeliPhone}
                                                errorText={t`population:phone_invalid`}
                                                placeholder="05X-XXXXXXX"
                                                icon={null}
                                                dir="ltr"
                                              />
                                           </StyledInputContainer>
                                           <StyledInputContainer>
                                             <ValidatedPopulationInput
                                                id="input-id-13"
                                                label={t`population:email`}
                                                value={value.email}
                                                onChange={(v: any) => update({ email: v })}
                                                disabled={disableEdit}
                                                placeholder=""
                                                required={false}
                                                setValidationError={error =>  setValidationError({ 'email': error }) }
                                                customValidation={(val: any) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/ig.test(val)}
                                                errorText={t`population:email_invalid`}
                                                icon={null}
                                              />       
                                           </StyledInputContainer>
                                       </SectionRow>
                                       <SectionRow>
                                        
                                           <StyledInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-16"
                                                label={t`population:mobile_extra`}
                                                value={displayPhone(value.extraMobile)}
                                                onChange={(v: any) => update({ extraMobile: normalizePhone(v) })}
                                                disabled={disableEdit}
                                                placeholder="05X-XXXXXXX"
                                                customValidation={(val: any) => val === '' || validateIsraeliPhone(val)}
                                                required={false}
                                                setValidationError={error => null}
                                                errorText={t`population:phone_invalid`}
                                                errorText=""
                                                icon={null}
                                                dir="ltr"
                                              />     
                                           </StyledInputContainer>
                                           <StyledInputContainer>
                                              <ValidatedPopulationInput
                                                id="input-id-15"
                                                label={t`population:phone_extra`}
                                                value={displayLandlinePhone(value.extraPhone)}
                                                onChange={(v: any) => update({ extraPhone: normalizeLandlinePhone(v) })}
                                                disabled={disableEdit}
                                                placeholder="0X-XXXXXXX"
                                                required={false}
                                                customValidation={(val: any) => val === '' || validateIsraeliLandlinePhone(val)}
                                                setValidationError={error => null}
                                                errorText={t`population:landline_invalid`}
                                                icon={null}
                                                dir="ltr"
                                              />
                                           </StyledInputContainer>
                                       </SectionRow>
                                       <SectionRow2>
                                           <UserTypeTitle>{t`population:user_types`}</UserTypeTitle>
                                            {selectedUserTypes.length == 0 && <UserTypeTitle>{t`population:add_user_type`}</UserTypeTitle>}
                                            <UserTypeListSelected>
                                                {selectedUserTypes.map((item) => <SelectedUserTypeButton onClick={() => { if (!disableEdit) removeUserTypeFromList(item) }} endIcon={!disableEdit && <Close />}>{item.tagName}</SelectedUserTypeButton>)}       
                                            </UserTypeListSelected>

                                            {!disableEdit && <UserTypeList>
                                               {userTypeList.map((tag) => <UserTypeButton onClick={() => addUserTypeToList(tag)} startIcon={<Add />}>{tag.tagName}</UserTypeButton>)}       
                                            </UserTypeList>}
                                           {!openNewUserTypeSection && !disableEdit && <NewUserTypeButton onClick={() => setOpenNewUserTypeSection(true)} startIcon={<Add />}>{t`population:create_user_type`}</NewUserTypeButton>}
                                           {openNewUserTypeSection && 
                                            <NewUserTypeContainer>
                                              <NewUserTypeInput     
                                                value={newTagName}                                
                                                placeholder={t`population:new_user_type_placeholder`}
                                                variant="outlined"
                                                onChange={e => setNewTagName(e.currentTarget.value)}
                                              />
                                              <NewUserTypeSaveButton onClick={() => addNewTag(newTagName)}>{t`population:add`}</NewUserTypeSaveButton>
                                              <NewUserTypeCancelButton onClick={() => { setOpenNewUserTypeSection(false); setNewTagName('')}}>{t`population:cancel`}</NewUserTypeCancelButton>
                                            </NewUserTypeContainer>}
                                       </SectionRow2>
           
                                       <SectionRow3>
                                         <UserTypeCheckSectionContainer>
                                           <UserTypeCheckSectionBlue>
                                               <Checkbox
                                                  checked={value.isLocalMember}
                                                  onChange={e => update({ isLocalMember: e.target.checked })}
                                                  disabled={value.isDead || disableEdit}
                                                  color='primary'
                                                  inputProps={{ 'aria-label': 'secondary checkbox' }}
                                                />
                                               <UserTypeCheckContainer>    
                                                   <UserTypeCheckTitle3>{t`population:local_member_title`}</UserTypeCheckTitle3>
                                                   <UserTypeCheckTitle4>{t`population:local_member_sub`}</UserTypeCheckTitle4>
                                               </UserTypeCheckContainer>
                                           </UserTypeCheckSectionBlue>
                                         </UserTypeCheckSectionContainer>
                                           
                                         <UserTypeCheckSectionContainer>
                                           <UserTypeCheckSection>
                                             <Checkbox
                                                checked={value.isDead}
                                                onChange={e => update({ isDead: e.target.checked })}
                                                color='primary'
                                                inputProps={{ 'aria-label': 'secondary checkbox' }}
                                                disabled={globalDisabled}
                                             />
                                             <UserTypeCheckContainer>    
                                               <UserTypeCheckTitle1>{t`population:deceased_title`}</UserTypeCheckTitle1>
                                               <UserTypeCheckTitle2>{t`population:deceased_sub`}</UserTypeCheckTitle2>
                                             </UserTypeCheckContainer>
                                           </UserTypeCheckSection>
                                           {value.isDead && 
                                             <div style={{ paddingTop: '10px', width: '100%' }}>
                                              <StyledLabel>{t`population:death_date`}</StyledLabel>
                                              <StyledDateInput
                                                type="date"
                                                value={value.deathDate ? new Date(value.deathDate).toISOString().split('T')[0] : ''}
                                                onChange={e => update({ deathDate: e.target.value ? new Date(e.target.value) : null })}
                                                disabled={globalDisabled}
                                              />
                                             </div>
                                           }
                                         </UserTypeCheckSectionContainer>
                                       </SectionRow3>
                                   </SectionPopulationContainer2>        
                               </SectionPopulation> 
  )
}

export default observer(PopulationSection)